import _ from "lodash";
import { useState } from "react";
import { CSVLink } from "react-csv";
import {
  useFilters,
  useFlexLayout,
  useGlobalFilter,
  usePagination,
  useTable,
} from "react-table";

import ButtonSecondary from "../../components/ButtonSecondary";
import { useNavigate } from "../../hooks/useNavigate";
import ChevronLeft from "../../icons/ChevronLeft";
import ChevronRight from "../../icons/ChevronRight";
import Download from "../../icons/Download";
import { appendClientIdToPath } from "../../lib/utility";
import DateRangePicker from "../DateRangePicker";
import usePaginationRange, { DOTS } from "../TablePagination";
import { DefaultFilterForColumn, GlobalFilter, QuickFilter } from "./Filter";

/**
 * It renders a table with pagination, filtering, and sorting
 * @returns A table component that is being rendered in the view.
 */
const Table = ({
  entity,
  data,
  columns,
  rowBehavior,
  globalFilterPlaceholder,
  quickFilters,
  csvFields,
  csvRow,
  dateFilter,
  onRowClick,
}) => {
  const navigate = useNavigate();

  // Create a state
  const [CSVData, setCSVData] = useState([]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    state,
    rows,
    prepareRow,
    setGlobalFilter,
    setFilter,
    preGlobalFilteredRows,
    flatRows,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex, pageSize },
    allColumns,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
      defaultColumn: { Filter: DefaultFilterForColumn },
      useFlexLayout,
    },
    useFilters,
    useGlobalFilter,
    usePagination
  );

  const siblingCount = 1;
  const totalPageCount = pageCount * pageSize;

  const paginationRange = usePaginationRange(
    totalPageCount,
    pageSize,
    siblingCount,
    pageIndex
  );

  const getCSVData = (fields) => {
    if (csvRow) {
      setCSVData(flatRows.map((row) => csvRow(row.original)));
      return;
    }

    // From https://konklone.io/json/ but updated to use ES6 and lodash
    const parse_object = (obj, path) => {
      if (path == undefined) path = "";

      const scalar =
        _.isNumber(obj) || _.isString(obj) || _.isBoolean(obj) || _.isNull(obj);

      if (_.isObject(obj)) {
        const d = {};
        for (const i in obj) {
          const newD = parse_object(obj[i], path + i + "/");
          _.extend(d, newD);
        }

        return d;
      } else if (scalar) {
        const d = {};
        const endPath = path.substr(0, path.length - 1);
        d[endPath] = obj;
        return d;
      }

      // ?
      else return {};
    };

    // Extract and parse data from from the filtered data
    const extractedCSVData = flatRows.map((row) => {
      // Extract desired fields
      let extractedData = _(row).chain().get("values").pick(fields).value();

      // Loop thru fields to parse objects
      fields.forEach((field) => {
        if (
          _(allColumns)
            .chain()
            .find((col) => col.id === field)
            .get("isObject")
            .eq(true)
            .value() &&
          extractedData[field]
        ) {
          // Check if the field is already an object (not a JSON string)
          if (typeof extractedData[field] === "object") {
            // Field is already an object, no need to parse
            return;
          }

          // Field is a JSON string, parse it
          try {
            const parsedField = JSON.parse(extractedData[field]);
            extractedData[field] = parsedField;
          } catch (error) {
            // If parsing fails, keep the original value
            console.warn(`Failed to parse field ${field}:`, error);
          }
        }
      });

      // Recursively flatten object
      const flattenedField = parse_object(extractedData);
      return flattenedField;
    });

    // Update state
    setCSVData(extractedCSVData);
  };

  return (
    <div className="flex flex-col gap-y-8">
      {/* Quick filter */}

      {quickFilters && (
        <QuickFilter
          setFilter={setFilter}
          filters={quickFilters}
          preGlobalFilteredRows={preGlobalFilteredRows}
        />
      )}

      <div className="flex flex-row items-center gap-x-4">
        {/* Global filter */}
        <GlobalFilter
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={state.globalFilter}
          setGlobalFilter={setGlobalFilter}
          globalFilterPlaceholder={globalFilterPlaceholder}
        />
        {dateFilter && (
          <DateRangePicker
            dateFilter={dateFilter}
            maxDays={dateFilter.maxDays}
          />
        )}
        {(csvFields || csvRow) && (
          <CSVLink
            filename={`${entity}-${new Date().toISOString()}.csv`}
            data={CSVData}
            onClick={() => {
              getCSVData(csvFields);
            }}
          >
            <ButtonSecondary icon={Download} text={"Export"} />
          </CSVLink>
        )}
      </div>
      {/* Table */}
      <div {...getTableProps()} className="flex flex-col">
        {/* Table header */}
        <TableHeader headerGroups={headerGroups} />

        {/* Table body */}
        <TableBody getTableBodyProps={getTableBodyProps}>
          {/* Loop through and render rows */}
          {page.map((row) => {
            prepareRow(row);
            return rowBehavior && rowBehavior.type == "link" ? (
              <a
                key={row.id}
                className="cursor-pointer"
                href={(() => {
                  const basePath = `${rowBehavior.config.path}/${
                    row.values[rowBehavior.config.resourceKey]
                  }`;
                  const searchParams = new URLSearchParams(
                    window.location.search
                  );
                  const clientId = searchParams.get("client");
                  return appendClientIdToPath(basePath, clientId || "");
                })()}
                rel="noopener noreferrer"
                // Still need to use onClick() to preserve app state during navigation and have faster navigation (href causes full page reload)
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(
                    `${rowBehavior.config.path}/${
                      row.values[rowBehavior.config.resourceKey]
                    }`
                  );
                }}
              >
                <TableRow row={row} />
              </a>
            ) : rowBehavior && rowBehavior.type == "onClick" ? (
              <div
                key={row.id}
                className="cursor-pointer"
                onClick={() => onRowClick(row.original)}
              >
                <TableRow row={row} />
              </div>
            ) : (
              <TableRow key={row.id} row={row} />
            );
          })}
        </TableBody>

        {/* Pagination */}
        <Pagination
          rows={rows}
          pageIndex={pageIndex}
          pageSize={pageSize}
          paginationRange={paginationRange}
          previousPage={previousPage}
          gotoPage={gotoPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
        />
      </div>
    </div>
  );
};

const TableHeader = ({ headerGroups }) => {
  return (
    <div>
      {/* Render header group */}
      {headerGroups.map((headerGroup) => {
        const { key: headerGroupKey, ...headerGroupProps } =
          headerGroup.getHeaderGroupProps({
            // style: { paddingRight: '15px' },
          });

        return (
          <div
            key={headerGroupKey}
            {...headerGroupProps}
            className="flex flex-row mb-3"
          >
            {/* Render each column */}
            {headerGroup.headers.map((column) => {
              const { key: headerKey, ...headerProps } = column.getHeaderProps([
                {
                  className:
                    "flex-auto uppercase text-[0.625rem] font-medium text-ellipsis overflow-hidden whitespace-nowrap px-2",
                },
                { className: column.className },
                {
                  style: { width: column.width },
                },
              ]);

              return (
                <div
                  key={headerKey}
                  // Column styling
                  {...headerProps}
                >
                  {/* Render header */}
                  {column.render("Header")}

                  {/* Render default column filter */}
                  <div>{column.canFilter ? column.render("Filter") : null}</div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

const TableBody = ({ getTableBodyProps, children }) => {
  return (
    <div
      {...getTableBodyProps()}
      //  Render table body
      className="overflow-y-scroll overflow-x-hidden divide-y"
    >
      {children}
    </div>
  );
};

const TableRow = ({ row }) => {
  const rowProps = _.omit(row.getRowProps(), "key");

  return (
    <div
      {...rowProps}
      // Row styling
      className="flex flex-col md:flex-row items-start transition hover:bg-gray-50 relative"
    >
      {/* Map and render cells */}
      {row.cells.map((cell) => {
        const hasOverflowVisible =
          cell.column.className?.includes("overflow-visible");

        const { key: cellKey, ...cellProps } = cell.getCellProps([
          { className: cell.column.className },
          {
            className: `flex-auto py-4 text-sm text-ellipsis ${
              hasOverflowVisible ? "overflow-visible" : "overflow-hidden"
            } whitespace-nowrap border-gray-200 px-2 ${
              !cell.column.isVisible && "hidden"
            }`,
          },
          {
            style: { width: cell.column.width },
          },
        ]);

        return (
          <div
            key={cellKey}
            // Cell styling
            {...cellProps}
          >
            {/* Render each cell */}
            {cell.render("Cell")}
          </div>
        );
      })}
    </div>
  );
};

const Pagination = ({
  rows,
  pageIndex,
  pageSize,
  paginationRange,
  previousPage,
  gotoPage,
  nextPage,
  canPreviousPage,
  canNextPage,
}) => {
  return (
    // For later - can we break this up?
    <div className="flex items-center justify-between py-3">
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        {/* Pagination info */}
        <div>
          <p className="text-sm text-gray-700">
            Showing{" "}
            {rows.length ? (
              <>
                <span key="range-of-records" className="font-medium">
                  {pageIndex * pageSize + 1}-
                  {(pageIndex + 1) * pageSize < rows.length
                    ? (pageIndex + 1) * pageSize
                    : rows.length}
                </span>{" "}
                of{" "}
              </>
            ) : (
              <></>
            )}
            <span key="total-records" className="font-medium">
              {rows.length}
            </span>{" "}
            records
          </p>
        </div>

        {/* Pagination nav */}
        <nav
          className="isolate inline-flex -space-x-px"
          aria-label="Pagination"
        >
          {/* Arrow left */}
          <a
            key="left-arrow"
            href="#"
            className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20"
            onClick={() => {
              previousPage();
            }}
            disabled={!canPreviousPage}
          >
            <span key="left-arrow-text" className="sr-only">
              Previous
            </span>
            <ChevronLeft className="w-2.5 h-2.5 fill-gray-500" />
          </a>

          {/* Page numbers */}
          {paginationRange.map((pageNumber) => {
            if (pageNumber === DOTS) {
              return (
                <span
                  key={"dots"}
                  className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700"
                >
                  {DOTS}
                </span>
              );
            }

            return (
              <a
                key={pageNumber}
                className={
                  pageIndex == pageNumber - 1
                    ? "relative z-10 inline-flex items-center border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 focus:z-20"
                    : "relative hidden items-center px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 md:inline-flex"
                }
                onClick={() => {
                  gotoPage(pageNumber - 1);
                }}
              >
                {pageNumber}
              </a>
            );
          })}

          {/* Arrow right */}
          <a
            key="right-arrow"
            href="#"
            className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20"
            onClick={() => {
              nextPage();
            }}
            disabled={!canNextPage}
          >
            <span key="right-arrow-text" className="sr-only">
              Next
            </span>
            <ChevronRight className="w-2.5 h-2.5 fill-gray-500" />
          </a>
        </nav>
      </div>
    </div>
  );
};

export default Table;
